import { useCallback, useEffect, useState } from 'react';
import useLatestRequest from '../../hooks/useLatestRequest';

/**
 * Loads one dashboard card's data on its own, so a card that fails or is slow never holds
 * up or blanks the others (the old dashboard awaited every call together and showed one
 * toast for any failure).
 *
 * `load` returns a promise of the card's data. It is re-run when anything in `deps`
 * changes (a calendar's month, say) and by the returned `reload` (a Retry button). Pass
 * plain values in `deps`, never AuthContext's helpers: they are rebuilt on every render.
 * Only the latest call may fill the card (useLatestRequest), so a late answer for the
 * previous month cannot overwrite the current one.
 *
 * @param {() => Promise<any>} load
 * @param {Array} deps
 * @returns {{ data: any, loading: boolean, error: Error|null, reload: () => void }}
 */
export default function useCardData(load, deps) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const beginRequest = useLatestRequest();

  // The caller's deps stand in for `load`, which is a new closure on every render. (The
  // rule reports on the dependency array, so that is the line the directive sits above.)
  const run = useCallback(async () => {
    const isCurrent = beginRequest();
    // Drop the previous answer: it belongs to the old deps (last month's events), and a
    // card must show it is loading, never an empty state or stale rows, until this lands.
    setState({ data: null, loading: true, error: null });
    try {
      const data = await load();
      if (isCurrent()) setState({ data, loading: false, error: null });
    } catch (error) {
      if (isCurrent()) setState({ data: null, loading: false, error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
  }, [run]);

  return { ...state, reload: run };
}
